<script setup lang="ts">
import { computed, ref } from 'vue';
import { TabOption } from '~/types';

interface Props {
  tabs: TabOption[];
  currentTab?: TabOption;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (event: 'update:currentTab', value: TabOption | undefined): void;
}>();

const localModel = ref<TabOption>(props.currentTab || props.tabs[0]);

const current = computed<TabOption>({
  get: () => props.currentTab || localModel.value,
  set: (value: TabOption) => {
    emit('update:currentTab', value);
    localModel.value = value;
  },
});

const handleUpdate = (tab: TabOption) => {
  current.value = tab;
};
</script>

<template>
  <div class="tabs">
    <template v-for="tab in tabs" :key="tab.value">
      <div
        v-if="tab?.visible === undefined || tab.visible"
        class="tabs__tab"
        :class="{ 'tabs__tab--active': tab.title === current.title }"
      >
        <slot :name="`tab-${tab.title}`" :active="tab.title === current.title" tab-class="tabs__tab-button">
          <button type="button" class="tabs__tab-button" @click="handleUpdate(tab)">{{ tab.title }}</button>
        </slot>
      </div>
      <slot :name="`after-tab-${tab.title}`" wrapper-class="tabs__tab" />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.tabs {
  @apply flex flex-col w-full;
  @apply xl:w-fit xl:flex-row xl:items-center xl:flex-wrap xl:gap-4;

  &__tab {
    @apply w-full;
    @apply xl:w-fit;

    &-button {
      @apply w-full px-5 py-4 bg-light-accent font-semibold text-[20px];
      @apply xl:w-fit xl:p-2 xl:bg-transparent xl:text-accent/40;
      @apply 3xl:text-[24px];
    }
  }

  &__tab--active &__tab-button {
    @apply bg-accent text-white;
    @apply xl:text-accent xl:bg-transparent;
  }
}
</style>
